import { useQuery } from '@tanstack/react-query'
import { GroupNote } from './GroupNote'
import { Panel } from '../../../components/Panel'
import { DeskFrame } from './DeskFrame'
import { NotYetLive } from './NotYetLive'
import { useDeskState } from './useDeskState'
import { deskApi, type DeskPub, type FlowColumn } from '../../../api/visitorsDesk'
import { MIN_NODE_PX, OUTBOUND_TODAY, STREAM_KEYS, nodeHeights, outboundLive, streamFromSearch, toggleStream, withStream, type Stream, type StreamKey } from '../../../lib/deskFlows'
import { fmtInt } from '../../../lib/format'

const th = 'px-2 py-1.5 text-left font-mono text-[11px] font-semibold uppercase tracking-wide text-paper-faint'
const td = 'px-2 py-1.5 align-top'
const mono = { fontVariantNumeric: 'tabular-nums' } as const
const OTHER_KEY = '(other)'
const COLUMN_TITLE: Record<StreamKey, string> = { channel: 'Came from', landing: 'Landed on', next_path: 'Went next', outcome: 'Ended' }
const pctCount = (rate: number | null, count: number | null) =>
  `${rate === null ? '—' : `${Math.round(rate * 100)}%`}${count === null ? '' : ` (${fmtInt(count)})`}`

function Loading() { return <div className="h-24 bg-ink-800 animate-pulse" /> }
function Failed({ what }: { what: string }) { return <p className="font-mono text-[12px] text-radar-red">Couldn’t load {what}</p> }

/** Grey tag for a panel the followed stream does not re-query. */
function NotFiltered({ stream, label }: { stream: Stream; label: (k: StreamKey, v: string) => string }) {
  const set = STREAM_KEYS.filter((k) => stream[k])
  if (set.length === 0) return null
  return (
    <div className="flex flex-wrap gap-2">
      {set.map((k) => <span key={k} className="border border-ink-500 px-1.5 py-0.5 font-mono text-[11px] text-paper-faint">Not filtered by {COLUMN_TITLE[k]}: {label(k, stream[k]!)}</span>)}
    </div>
  )
}

export function DeskFlows() {
  return <DeskFrame tab="flows"><Body /></DeskFrame>
}

function Body() {
  const { pub, q, spKey, setSp } = useDeskState('flows')
  const p = pub as DeskPub
  const stream = streamFromSearch(new URLSearchParams(spKey))
  const skey = JSON.stringify(stream)
  const key = [pub, q.period, q.from, q.to, q.compare, q.group]
  const head = useQuery({ queryKey: ['desk', 'flows', ...key, skey], queryFn: () => deskApi.flows(p, q, stream), retry: false, placeholderData: (prev) => prev })
  const setStream = (s: Stream) => setSp(withStream(spKey, s))

  if (head.isError) return <Failed what="the flows" />
  if (!head.data) return <Loading />
  const d = head.data
  if (d.unavailable !== undefined) return <NotYetLive text={d.unavailable} />

  // The label a followed stream shows: the block's own label while it is on screen, else its key.
  const labelOf = (k: StreamKey, v: string) => d.columns.find((c) => c.column === k)?.nodes.find((n) => n.key === v)?.label ?? v

  return (
    <div className="space-y-4">
      <Panel label={d.heading} accent="amber" right={<span className="font-mono text-[11px] text-paper-faint">{d.note}</span>}>
        <div className="space-y-3">
          <StreamPills stream={stream} label={labelOf} onChange={setStream} />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4 md:gap-7" role="group" aria-label="Traffic flow, four steps">
            {d.columns.map((c) => <Column key={c.column} col={c} stream={stream} onPick={(k) => setStream(toggleStream(stream, c.column, k))} />)}
          </div>
          <h3 className="pt-2 font-mono text-[11px] font-semibold uppercase tracking-wide text-paper-faint">{d.paths_heading}</h3>
          <ul className="space-y-2">
            {d.paths.map((r, i) => (
              <li key={i} className="flex flex-wrap items-center gap-1.5 text-[13px]">
                <span className="min-w-[44px] font-mono" style={mono}>{fmtInt(r.visits)}</span>
                {r.steps.map((s, j) => (
                  <span key={j} className="flex items-center gap-1.5">
                    <span className="max-w-[240px] truncate bg-ink-700 px-2 py-0.5" title={s}>{s}</span>
                    <span aria-hidden="true" className="text-paper-faint">→</span>
                  </span>
                ))}
                <strong>{r.outcome_label}</strong>
              </li>
            ))}
            {d.paths.length === 0 && <li className="text-paper-faint">No complete paths in this period</li>}
          </ul>
        </div>
      </Panel>
      <Landing p={p} q={q} keyBase={key} stream={stream} label={labelOf} />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Exits p={p} q={q} keyBase={key} stream={stream} label={labelOf} />
        <Outbound p={p} q={q} keyBase={key} stream={stream} label={labelOf} />
      </div>
    </div>
  )
}

/** The followed stream as pills. Channel and landing also show in the frame's filter pills; every step is removable here. */
function StreamPills({ stream, label, onChange }: { stream: Stream; label: (k: StreamKey, v: string) => string; onChange: (s: Stream) => void }) {
  const set = STREAM_KEYS.filter((k) => stream[k])
  if (set.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-[11px] text-paper-faint">Following</span>
      {set.map((k) => (
        <button key={k} type="button" aria-label={`Stop following ${COLUMN_TITLE[k]}: ${label(k, stream[k]!)}`}
                onClick={() => { const n = { ...stream }; delete n[k]; onChange(n) }}
                className="max-w-[320px] truncate border border-amber-glow px-2 py-1 font-mono text-[12px] text-amber-glow hover:bg-ink-700">
          {COLUMN_TITLE[k]}: {label(k, stream[k]!)} ×
        </button>
      ))}
    </div>
  )
}

function Column({ col, stream, onPick }: { col: FlowColumn; stream: Stream; onPick: (key: string) => void }) {
  const heights = nodeHeights(col.nodes, 160)
  const total = col.nodes.reduce((a, n) => a + n.visits, 0)
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="mb-1 font-mono text-[11px] font-semibold uppercase tracking-wide text-paper-faint">{col.name}</div>
      {col.nodes.map((n, i) => {
        const body = (
          <>
            <span className="block truncate" title={n.label}>{n.label}</span>
            <span className="font-mono text-[12px] text-paper-dim" style={mono}>{fmtInt(n.visits)}</span>
          </>
        )
        const cls = 'flex w-full flex-col justify-center px-2.5 py-1 text-left text-[13px] text-paper'
        const style = { minHeight: Math.max(MIN_NODE_PX, heights[i]) }
        if (n.key === OTHER_KEY) return <div key={n.key} className={`${cls} border border-ink-600 bg-ink-800`} style={style}>{body}</div>
        const on = stream[col.column] === n.key
        return (
          <button key={n.key} type="button" aria-pressed={on} onClick={() => onPick(n.key)} style={style}
                  className={`${cls} border ${on ? 'border-amber-glow bg-ink-600' : 'border-ink-500 bg-ink-700 hover:bg-ink-600'}`}>
            {body}
          </button>
        )
      })}
      {col.nodes.length === 0 && <div className="text-[13px] text-paper-faint">No visits</div>}
      <span className="sr-only">{col.name}: {fmtInt(total)} visits in {col.nodes.length} blocks</span>
    </div>
  )
}

type Q = ReturnType<typeof useDeskState>['q']
interface SubProps { p: DeskPub; q: Q; keyBase: unknown[]; stream: Stream; label: (k: StreamKey, v: string) => string }

function Landing({ p, q, keyBase, stream, label }: SubProps) {
  const query = useQuery({ queryKey: ['desk', 'flows-landing', ...keyBase], queryFn: () => deskApi.flowsLanding(p, q), retry: false })
  const d = query.data
  return (
    <Panel label="Landing pages · what happens after people arrive" accent="cyan">
      {query.isError ? <Failed what="landing pages" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
        <div className="space-y-2">
          <NotFiltered stream={stream} label={label} />
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] text-[13px]">
              <thead>
                <tr>
                  <th className={th}>Landed on</th><th className={th}>Mostly from</th>
                  <th className={`${th} text-right`}>Arrivals</th><th className={`${th} text-right`}>Read a 2nd page</th>
                  <th className={th}>Most common next page</th><th className={`${th} text-right`}>Hit the paywall</th>
                  <th className={`${th} text-right`}>Signed up / joined</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.landing} className="border-t border-ink-600">
                    <td className={`${td} font-semibold`}><span className="block max-w-[260px] truncate" title={r.landing}>{r.title ?? r.landing}</span></td>
                    <td className={`${td} text-paper-faint`}><span className="block max-w-[180px] truncate" title={r.mostly_from}>{r.mostly_from}</span></td>
                    <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.arrivals)}</td>
                    <td className={`${td} text-right`}>
                      <span className={`font-mono ${r.low_recirculation ? 'text-amber-glow' : ''}`} style={mono}>{pctCount(r.second_page_rate, r.second_page)}</span>
                      {r.low_recirculation && <div className="font-mono text-[11px] text-amber-glow">low: most leave after one page</div>}
                    </td>
                    <td className={td}><span className="block max-w-[220px] truncate" title={r.top_next ?? ''}>{r.top_next_title ?? r.top_next ?? '—'}</span></td>
                    <td className={`${td} text-right font-mono`} style={mono}>{r.paywall_rate === null ? '—' : pctCount(r.paywall_rate, r.paywall_hits)}</td>
                    <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.signed_up)} / {fmtInt(r.joined)}</td>
                  </tr>
                ))}
                {d.rows.length === 0 && <tr><td colSpan={7} className={`${td} text-paper-faint`}>No arrivals in this period</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-[12px] text-paper-faint">{d.note}</p>
        </div>
      )}
    </Panel>
  )
}

function Exits({ p, q, keyBase, stream, label }: SubProps) {
  const query = useQuery({ queryKey: ['desk', 'flows-exits', ...keyBase], queryFn: () => deskApi.flowsExits(p, q), retry: false })
  const d = query.data
  return (
    <Panel label="Where people left" accent="violet">
      {query.isError ? <Failed what="exits" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
        <div className="space-y-2">
          <NotFiltered stream={stream} label={label} />
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-[13px]">
              <thead><tr><th className={th}>Last page seen</th><th className={`${th} text-right`}>Exits</th><th className={`${th} text-right`}>of its views</th><th className={th}>Usual reason</th></tr></thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.path} className="border-t border-ink-600">
                    <td className={td}><span className="block max-w-[240px] truncate" title={r.path}>{r.title ?? r.path}</span></td>
                    <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.exits)}</td>
                    <td className={`${td} text-right font-mono`} style={mono} title={r.views === null ? undefined : `of ${fmtInt(r.views)} views`}>{r.share_of_views === null ? '—' : pctCount(r.share_of_views, r.views)}</td>
                    <td className={`${td} text-paper-faint`}>{r.reason ?? '—'}</td>
                  </tr>
                ))}
                {d.rows.length === 0 && <tr><td colSpan={4} className={`${td} text-paper-faint`}>No exits in this period</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Panel>
  )
}

function Outbound({ p, q, keyBase, stream, label }: SubProps) {
  const enabled = outboundLive(q.period)
  const query = useQuery({ queryKey: ['desk', 'flows-outbound', ...keyBase], queryFn: () => deskApi.flowsOutbound(p, q), retry: false, enabled })
  const d = query.data
  return (
    <Panel label="Links clicked out of our site" accent="green">
      {!enabled ? <p className="text-[13px] text-paper-faint">{OUTBOUND_TODAY}</p> : query.isError ? <Failed what="outbound links" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
        <div className="space-y-2">
          <GroupNote applied={d.group_applied} />
          <NotFiltered stream={stream} label={label} />
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-[13px]">
              <thead><tr><th className={th}>Went to</th><th className={th}>From article</th><th className={`${th} text-right`}>Clicks</th><th className={th}>Kind</th></tr></thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={`${r.url}-${r.from_path}`} className="border-t border-ink-600">
                    <td className={td}><span className="block max-w-[240px] truncate" title={r.url}>{r.url}</span></td>
                    <td className={td}><span className="block max-w-[200px] truncate" title={r.from_path}>{r.from_title ?? r.from_path}</span></td>
                    <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.clicks)}</td>
                    <td className={`${td} text-paper-faint`}>{r.kind}</td>
                  </tr>
                ))}
                {d.rows.length === 0 && <tr><td colSpan={4} className={`${td} text-paper-faint`}>No outbound clicks in this period</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-[12px] text-paper-faint">{d.note}</p>
        </div>
      )}
    </Panel>
  )
}
